import React, { FunctionComponent, useEffect, useState } from 'react';

import { GetWorkspacesResponse, WorkspaceData } from '@components/show_page/bulk_actions/bulk-workspace-modal/BulkWorkspaceModalTypes';
import { SelectWorkspacesView } from '@components/show_page/bulk_actions/bulk-workspace-modal/SelectWorkspacesView';
import { useFetch, UseFetchOptions } from '@api/ApiHelper';
import { StandardDialog } from '@components/library/dialog';
import { PrimaryButton } from '@components/library/button';
import { track as trackLogRocket, TrackedEvent } from '@helpers/logRocket';
import { BFLoader } from '@components/common/loader/main';
import { FontIcons } from '@components/library/icon';
import { WorkspaceCreateForm } from '@components/show_page/sections/navbar-admin/WorkspaceCreateForm';
import './styles/bulk-workspace-modal.scss';

interface BulkWorkspaceProps {
  assetKeys: string[];
  open: boolean;
  setOpen: (open: boolean) => void;
  /** show search input in UI when workspace count exceeds this number */
  showSearchWorkspaceCount?: number;
}

enum ContentState {
  IsLoading,
  LoadedWithWorkspaces,
  LoadedWithNoWorkspaces,
}

const getWorkspacesOptions = {
  params: { is_workspace: true, per: 1000 }, // eslint-disable-line @typescript-eslint/naming-convention
  url: `/api/v4/brandfolders/${BFG.brandfolder_key}/collections`,
};

const postWorkspacesOptions = (assetKeys: string[], selectedWorkspaceKeys: string[]): UseFetchOptions => ({
  body: {
    data: {
      asset_keys: [...assetKeys], // eslint-disable-line @typescript-eslint/naming-convention
      brandfolder_key: BFG.brandfolder_key, // eslint-disable-line @typescript-eslint/naming-convention
      collection_keys: [...selectedWorkspaceKeys], // eslint-disable-line @typescript-eslint/naming-convention
    }
  },
  fetchOnMount: false,
  method: 'POST',
  url: '/api/v4/bulk_actions/assets/collection_associations',
});

export const BulkWorkspaceModal: FunctionComponent<BulkWorkspaceProps> = ({
  assetKeys,
  open,
  setOpen,
  showSearchWorkspaceCount = 10
}) => {
  const [workspaces, setWorkspaces] = useState<WorkspaceData[]>([]);
  const [selectedWorkspaceKeys, setSelectedWorkspaceKeys] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const getWorkspacesResponse = useFetch<GetWorkspacesResponse>(getWorkspacesOptions);
  const postWorkspacesResponse = useFetch<{ result: string } | Error>(postWorkspacesOptions(assetKeys, selectedWorkspaceKeys));

  const plural = assetKeys.length > 1;
  const titleCopy = plural
    ? `${bfTranslate('Add')} ${assetKeys.length} ${bfTranslate('assets to a Workspace')}`
    : `${bfTranslate('Add')} ${assetKeys.length} ${bfTranslate('asset to a Workspace')}`;

  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const renderNoWorkspaces = (
    <div>
      <p className='no-workspace-message'>{bfTranslate('No workspaces were found.')}</p>
      <WorkspaceCreateForm
        brandfolderSlug={BFG.brandfolder_slug}
        isOpenDialog={isOpenDialog}
        setIsOpenDialog={setIsOpenDialog}
      />
    </div>
  );

  const contentState = (): ContentState => {
    if (!getWorkspacesResponse.loading && workspaces.length) return ContentState.LoadedWithWorkspaces;
    if (!getWorkspacesResponse.loading && !workspaces.length) return ContentState.LoadedWithNoWorkspaces;
    // default to loading state
    return ContentState.IsLoading;
  };

  useEffect(() => {
    if (getWorkspacesResponse.response) setWorkspaces(getWorkspacesResponse.response.data);
  }, [getWorkspacesResponse.response]);

  useEffect(() => {
    if (postWorkspacesResponse.response) {
      const assetCopy = assetKeys.length > 1 ? bfTranslate('assets') : bfTranslate('asset');
      const workspaceCopy = selectedWorkspaceKeys.length > 1 ? bfTranslate('workspaces') : bfTranslate('workspace');

      Notify.create({
        title: `${assetKeys.length} ${assetCopy} ${bfTranslate('added to')} ${selectedWorkspaceKeys.length} ${workspaceCopy}`,
        type: 'success'
      });
      trackLogRocket(TrackedEvent.WorkspaceAssetAdd);
      setOpen(false);
    }
  }, [postWorkspacesResponse.response]);

  useEffect(() => {
    if (postWorkspacesResponse.error) {
      Notify.create({
        title: bfTranslate('Error adding assets to workspaces. Please try again.'),
        type: 'error'
      });
      trackLogRocket(TrackedEvent.WorkspaceAssetAddError);
    }
  }, [postWorkspacesResponse.error]);

  const selectWorkspacesViewProps = {
    searchValue,
    selectedWorkspaceKeys,
    setSearchValue,
    setSelectedWorkspaceKeys,
    showSearchWorkspaceCount,
    workspaces,
  };

  return (
    <StandardDialog
      footer={(
        <PrimaryButton
          isLoading={getWorkspacesResponse.loading || postWorkspacesResponse.loading}
          loadingCopy={getWorkspacesResponse.loading ? bfTranslate('Loading') : bfTranslate('Submitting')}
          onClick={(): void => { 
            if (workspaces.length === 0) {
              setIsOpenDialog(true);
            } else {
              postWorkspacesResponse.fetch();
            }
          }}
          size="medium"
        >
          {workspaces.length === 0 ? bfTranslate('Create a Workspace') : bfTranslate('Submit')}
        </PrimaryButton>
      )}
      id="bulk-workspace-dialog"
      open={open}
      setOpen={setOpen}
      showFooter
      title={titleCopy}
      titleIcon={FontIcons.Plus}
      width={660}
    >
      <div className="bulk-workspace-dialog__content">
        {{
          [ContentState.IsLoading]: <BFLoader />,
          [ContentState.LoadedWithWorkspaces]: SelectWorkspacesView(selectWorkspacesViewProps),
          [ContentState.LoadedWithNoWorkspaces]: renderNoWorkspaces,
        }[contentState()]}
      </div>
    </StandardDialog>
  );
};
